import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ModuleViewProps } from '../../shell/types';
import {
  Button,
  Card,
  DataTable,
  Field,
  Id,
  Metric,
  Notice,
  ModulePage,
  Select,
  StatusBadge,
  TextInput,
  Moment,
  type Column,
} from '../../shell/kit';
import { useToast } from '../../shell/toast';
import { TemplatesView } from './templates';
import { TriggersView } from './triggers';
import { CustomerPicker } from '../../shell/pickers';
import { useTab } from '../../shell/hooks';
import {
  broadcast,
  listSegments,
  listTemplates,
  messageLog,
  providersState,
  saveTemplate,
  sendOne,
  type CommsTemplate,
  type MessageLogItem,
  type BroadcastResult,
  type ProvidersState,
  type SegmentOption,
  type SendResult,
} from './api';
import { makeCommsT, type CommsKey, type CommsLocale } from './i18n';
import { errorText } from '../../shell/errors';

type TabKey = 'templates' | 'triggers' | 'log' | 'broadcast' | 'providers';

const TABS: ReadonlyArray<{ key: TabKey; label: CommsKey; needs: 'send' | 'view' | 'admin' }> = [
  { key: 'templates', label: 'comms.tab.templates', needs: 'send' },
  { key: 'triggers', label: 'comms.tab.triggers', needs: 'send' },
  { key: 'broadcast', label: 'comms.tab.broadcast', needs: 'send' },
  { key: 'log', label: 'comms.tab.log', needs: 'view' },
  { key: 'providers', label: 'comms.tab.providers', needs: 'admin' },
];

const SEND_ROLES = new Set(['marketer', 'network_admin']);
const VIEW_ROLES = new Set(['cashier', 'operator', 'site_manager', 'marketer', 'network_admin', 'auditor']);

/**
 * Сообщение об ошибке показывается тостом рядом с действием. Раньше все ответы
 * рисовались плашкой вверху страницы, а кнопки стояли внизу длинной формы: экран
 * внешне не менялся, и человек нажимал второй раз.
 */
function useFail(toast: ReturnType<typeof useToast>) {
  return (err: unknown): void => {
    const shown = errorText(err);
    toast.error(shown.message, shown.code);
  };
}

function LogView({ t, locale }: { t: ReturnType<typeof makeCommsT>; locale: CommsLocale }) {
  const [rows, setRows] = useState<readonly MessageLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [customerId, setCustomerId] = useState('');
  const [status, setStatus] = useState('');
  const toast = useToast();
  const fail = useFail(toast);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await messageLog({ customerId: customerId.trim() || undefined, status: status || undefined })).items);
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, [customerId, status, locale]);

  /*
   * Фильтры применяются сами. Так работают остальные разделы админки, и с
   * пикером это стало заметно: человек выбирает клиента из списка — действие
   * законченное, — а таблица под ним не меняется, пока не нажата ещё кнопка.
   */
  useEffect(() => {
    void load();
  }, [load]);

  const columns: ReadonlyArray<Column<MessageLogItem>> = [
    {
      // Экран существует ради ответа на вопрос «кому это ушло». Без получателя
      // на жалобу «мне пришла реклама» ответить нельзя, не зная заранее ULID клиента.
      key: 'to',
      header: t('comms.log.recipient'),
      render: (r) => (
        <div className="min-w-40">
          <div className="text-ink">
            {r.employeeName ? `${r.employeeName} · ${t('comms.log.staff')}` : (r.customerName ?? t('comms.log.customer'))}
          </div>
          <div className="font-mono text-xs text-ink-muted">{r.recipientMasked}</div>
          {r.customerId ? <Id value={r.customerId} /> : null}
        </div>
      ),
    },
    { key: 'ch', header: t('comms.templates.channel'), render: (r) => r.channel },
    { key: 'tpl', header: t('comms.templates.code'), render: (r) => <code className="text-xs">{r.templateCode ?? '—'}</code> },
    { key: 'kind', header: t('comms.templates.kind'), render: (r) => t(`comms.kind.${r.templateKind}` as CommsKey) },
    {
      key: 'st',
      header: t('comms.log.status'),
      render: (r) => (
        <StatusBadge
          tone={
            r.status === 'delivered' || r.status === 'sent'
              ? 'ok'
              : r.status === 'failed'
                ? 'danger'
                : r.status === 'suppressed'
                  ? 'warn'
                  : 'neutral'
          }
        >
          {t(`comms.status.${r.status}` as CommsKey)}
        </StatusBadge>
      ),
    },
    { key: 'basis', header: t('comms.log.basis'), render: (r) => t(`comms.basis.${r.consentBasis}` as CommsKey) },
    { key: 'consent', header: t('comms.log.consent'), render: (r) => (r.consentId ? '✓' : '—') },
    { key: 'reason', header: t('comms.log.reason'), render: (r) => <span className="text-xs">{r.reason}</span> },
    {
      key: 'why',
      header: t('comms.log.suppressed'),
      // Пустая причина при статусе «ошибка» ни о чём не говорит: у whatsapp она
      // была пустой, а на деле канал просто не настроен.
      render: (r) =>
        r.status === 'failed' || r.status === 'suppressed' ? (
          <span className="text-xs text-danger">
            {r.suppressedReason ?? t('comms.log.noReason')}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    { key: 'at', header: t('comms.log.created'), render: (r) => <Moment iso={r.createdAt} /> },
  ];

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('comms.log.hint')}</Notice>

      <Card title={t('comms.log.title')}>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          {/*
            * Фильтр по клиенту — тот же пикер, что и в идентификации. Здесь
            * стояло свободное поле под идентификатор: чтобы посмотреть, что
            * человеку отправляли, нужно было сначала где-то раздобыть его ULID.
            */}
          <Field label={t('comms.log.customer')} hint={t('comms.log.customerHint')}>
            <div className="w-72">
              <CustomerPicker value={customerId} onChange={(id) => setCustomerId(id)} />
            </div>
          </Field>
          <Field label={t('comms.log.status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">—</option>
              {['queued', 'sent', 'delivered', 'failed', 'suppressed'].map((s) => (
                <option key={s} value={s}>{t(`comms.status.${s}` as CommsKey)}</option>
              ))}
            </Select>
          </Field>
          <Button onClick={() => void load()} disabled={loading}>
            {t('comms.log.refresh')}
          </Button>
        </div>
        <DataTable
          loading={loading}
          rows={rows}
          columns={columns}
          empty={t('comms.common.nothing')}
          rowKey={(r) => r.id}
        />
      </Card>
    </div>
  );
}

/**
 * Рассылка по сегменту.
 *
 * Две вещи здесь были сломаны насквозь. Во-первых, шаблон с переменными отправить
 * было нельзя: форма состояла из «Сегмент / Шаблон / Основание», а сервер требовал
 * значения для {name}, {percent}, {until} — то есть ровно те шаблоны, ради которых
 * подстановки и делали, оставались неотправляемыми. Во-вторых, пустая рассылка
 * показывалась зелёным «получателей 0»: маркетолог видел успех там, где не ушло
 * ни одного сообщения.
 */
function BroadcastView({ t, locale }: { t: ReturnType<typeof makeCommsT>; locale: CommsLocale }) {
  const toast = useToast();
  const [segments, setSegments] = useState<readonly SegmentOption[]>([]);
  const [templates, setTemplates] = useState<readonly CommsTemplate[]>([]);
  const [segmentId, setSegmentId] = useState('');
  const [template, setTemplate] = useState('');
  const [reason, setReason] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<BroadcastResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setSegments((await listSegments()).segments);
        setTemplates((await listTemplates()).templates);
      } catch (err) {
        toast.error(errorText(err).message, errorText(err).code);
      }
    })();
  }, [locale, toast]);

  // Переменные берутся из выбранного шаблона: в разных локалях один код, поэтому
  // объединяем объявленные имена по всем версиям шаблона.
  const declared = useMemo(() => {
    const names = new Set<string>();
    for (const row of templates) {
      if (row.code === template) row.variables.forEach((name) => names.add(name));
    }
    return [...names].sort();
  }, [templates, template]);

  const missing = declared.filter((name) => !(values[name] ?? '').trim());
  const canSend = Boolean(segmentId) && Boolean(template) && reason.trim().length >= 3 && missing.length === 0;

  const run = async (): Promise<void> => {
    setBusy(true);
    setResult(null);
    try {
      const variables: Record<string, unknown> = {};
      for (const name of declared) variables[name] = values[name] ?? '';
      const outcome = await broadcast({ segmentId, template, variables, reason });
      setResult(outcome);
      if (outcome.queued === 0) {
        toast.warn(t('comms.broadcast.empty'));
      } else {
        toast.ok(
          t('comms.broadcast.result', {
            requested: outcome.requested,
            queued: outcome.queued,
            suppressed: outcome.suppressed,
          }),
        );
      }
    } catch (err) {
      toast.error(errorText(err).message, errorText(err).code);
    } finally {
      setBusy(false);
    }
  };

  const codes = [...new Set(templates.map((x) => x.code))];

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('comms.broadcast.hint')}</Notice>

      <Card title={t('comms.broadcast.title')}>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label={t('comms.broadcast.segment')}>
            <Select
              value={segmentId}
              onChange={(e) => {
                setSegmentId(e.target.value);
                setResult(null);
              }}
            >
              <option value="">—</option>
              {segments.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.lastSize === null ? '' : `(${s.lastSize})`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('comms.broadcast.template')}>
            <Select
              value={template}
              onChange={(e) => {
                setTemplate(e.target.value);
                setValues({});
                setResult(null);
              }}
            >
              <option value="">—</option>
              {codes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('comms.broadcast.reason')} required>
            <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>

        {template ? (
          <div className="mt-4 rounded border border-line bg-sunken p-3">
            <p className="text-sm font-medium text-ink">{t('comms.broadcast.variables')}</p>
            {declared.length === 0 ? (
              <p className="mt-1 text-sm text-ink-muted">{t('comms.broadcast.variablesNone')}</p>
            ) : (
              <>
                <p className="mt-0.5 text-xs text-ink-muted">
                  {t('comms.broadcast.variablesHint')}
                </p>
                <div className="mt-2 grid gap-3 md:grid-cols-3">
                  {declared.map((name) => (
                    <Field key={name} label={`{${name}}`} required>
                      <TextInput
                        value={values[name] ?? ''}
                        onChange={(e) => setValues({ ...values, [name]: e.target.value })}
                      />
                    </Field>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}

        <div className="mt-3">
          <Button tone="primary" disabled={!canSend || busy} onClick={() => void run()}>
            {t('comms.broadcast.send')}
          </Button>
        </div>
      </Card>

      <SingleSend t={t} templates={templates} />

      {result ? (
        <Card title={t('comms.broadcast.report')}>
          <div className="grid gap-3 sm:grid-cols-3">
            <Metric label={t('comms.broadcast.recipients')} value={result.requested} />
            <Metric
              label={t('comms.broadcast.queued')}
              value={result.queued}
              tone={result.queued > 0 ? 'ok' : 'warn'}
            />
            <Metric
              label={t('comms.broadcast.notSent')}
              value={result.suppressed}
              tone={result.suppressed > 0 ? 'warn' : 'neutral'}
            />
          </div>
          <p className="mt-3 text-xs text-ink-muted">
            {t(`comms.broadcast.mode.${result.segmentMode}` as CommsKey)}
          </p>
          {result.queued === 0 ? (
            <div className="mt-3">
              <Notice tone="warn">{t('comms.broadcast.empty')}</Notice>
            </div>
          ) : null}
          {Object.keys(result.suppressedByReason).length > 0 ? (
            <ul className="mt-3 space-y-1 text-sm text-ink-muted">
              {Object.entries(result.suppressedByReason).map(([why, count]) => (
                <li key={why} className="flex justify-between gap-3 border-b border-line py-1">
                  <span>{why}</span>
                  <span className="tabular font-medium text-ink">{count}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Отправка одному.
 *
 * Рассылка по сегменту была, точечной отправки — нет, хотя маршрут её умеет.
 * Практический случай: клиент позвонил, вопрос решили, надо подтвердить
 * письменно. Согласие проверяет сервер — если шаблон маркетинговый, а подписки
 * нет, вернётся отказ с причиной, и это правильный ответ, а не ошибка.
 */
function SingleSend({
  t,
  templates,
}: {
  t: ReturnType<typeof makeCommsT>;
  templates: readonly CommsTemplate[];
}) {
  const toast = useToast();
  const [customerId, setCustomerId] = useState('');
  const [template, setTemplate] = useState('');
  const [reason, setReason] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<SendResult | null>(null);
  const [busy, setBusy] = useState(false);

  const declared = useMemo(() => {
    const names = new Set<string>();
    for (const row of templates) {
      if (row.code === template) row.variables.forEach((name) => names.add(name));
    }
    return [...names].sort();
  }, [templates, template]);

  const codes = [...new Set(templates.map((x) => x.code))];
  const missing = declared.filter((name) => !(values[name] ?? '').trim());
  const ready =
    Boolean(customerId) && Boolean(template) && reason.trim().length >= 3 && missing.length === 0;

  async function run(): Promise<void> {
    setBusy(true);
    setOutcome(null);
    try {
      const variables: Record<string, unknown> = {};
      for (const name of declared) variables[name] = values[name] ?? '';
      const result = await sendOne({ customerId, template, variables, reason: reason.trim() });
      setOutcome(result);
      if (result.status === 'queued') toast.ok(t('comms.single.queued'));
      else toast.warn(t('comms.single.suppressed'));
    } catch (err) {
      toast.error(errorText(err).message, errorText(err).code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('comms.single.title')}>
      <p className="mb-3 text-sm text-ink-muted">{t('comms.single.hint')}</p>

      <div className="grid gap-3 md:grid-cols-3">
        <Field label={t('comms.single.customer')} hint={t('comms.log.customerHint')}>
          <CustomerPicker
            value={customerId}
            onChange={(id) => {
              setCustomerId(id);
              setOutcome(null);
            }}
          />
        </Field>
        <Field label={t('comms.broadcast.template')}>
          <Select
            value={template}
            onChange={(e) => {
              setTemplate(e.target.value);
              setValues({});
              setOutcome(null);
            }}
          >
            <option value="">—</option>
            {codes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('comms.broadcast.reason')} required>
          <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>

      {declared.length > 0 ? (
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          {declared.map((name) => (
            <Field key={name} label={`{${name}}`} required>
              <TextInput
                value={values[name] ?? ''}
                onChange={(e) => setValues({ ...values, [name]: e.target.value })}
              />
            </Field>
          ))}
        </div>
      ) : null}

      <div className="mt-3">
        <Button tone="primary" disabled={!ready || busy} onClick={() => void run()}>
          {t('comms.single.send')}
        </Button>
      </div>

      {outcome ? (
        <div className="mt-3">
          <Notice tone={outcome.status === 'queued' ? 'ok' : 'warn'}>
            {outcome.status === 'queued'
              ? t('comms.single.queuedTo', { channel: outcome.channel })
              : t('comms.single.suppressedWhy', {
                  reason: outcome.suppressedReason ?? '—',
                })}
          </Notice>
        </div>
      ) : null}
    </Card>
  );
}

function ProvidersView({ t, locale }: { t: ReturnType<typeof makeCommsT>; locale: CommsLocale }) {
  const [state, setState] = useState<ProvidersState | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const fail = useFail(toast);

  useEffect(() => {
    void (async () => {
      try {
        setState(await providersState());
      } catch (err) {
        fail(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [locale]);

  const inbound = state?.inbound ?? {};

  return (
    <div className="space-y-4">
      <Card title={t('comms.providers.title')}>
        <DataTable
          loading={loading}
          rows={state?.providers ?? []}
          columns={[
            { key: 'ch', header: t('comms.providers.channel'), render: (r) => r.channel },
            { key: 'n', header: t('comms.providers.name'), render: (r) => r.name },
            {
              key: 'ok',
              header: t('comms.providers.state'),
              render: (r) => (
                <span className={r.ok ? 'text-ok' : 'text-danger'}>
                  {r.ok ? t('comms.providers.ok') : t('comms.providers.down')}
                </span>
              ),
            },
            {
              key: 'wh',
              header: t('comms.providers.webhook'),
              render: (r) => (r.webhook ? t('comms.providers.webhookSigned') : t('comms.providers.webhookClosed')),
            },
            { key: 'd', header: t('comms.providers.detail'), render: (r) => r.detail },
          ]}
          empty={t('comms.common.nothing')}
          rowKey={(r) => `${r.channel}:${r.name}`}
        />
      </Card>

      {state ? (
        <Card title={t('comms.providers.inbound')}>
          <ul className="space-y-1 text-sm text-ink">
            {(['forwarded', 'received', 'ignored', 'failed'] as const).map((k) => (
              <li key={k} className={k === 'ignored' || k === 'failed' ? ((inbound[k] ?? 0) > 0 ? 'text-warn' : '') : ''}>
                {t(`comms.providers.inbound.${k}` as CommsKey, { n: inbound[k] ?? 0 })}
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <Notice tone={state.alertmanager ? 'ok' : 'warn'} title={t('comms.providers.alertmanager')}>
              {state.alertmanager ? t('comms.providers.alertmanagerOn') : t('comms.providers.alertmanagerOff')}
            </Notice>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

export default function CommsModule({ user, title }: ModuleViewProps) {
  const locale: CommsLocale = user.locale === 'kk' ? 'kk' : 'ru';
  const t = useMemo(() => makeCommsT(locale), [locale]);

  const roles = useMemo(() => new Set(user.bindings.map((b) => b.role)), [user.bindings]);
  const networkWide = useMemo(() => user.bindings.some((b) => b.siteId === null), [user.bindings]);

  const canSend = networkWide && [...roles].some((r) => SEND_ROLES.has(r));
  const canView = [...roles].some((r) => VIEW_ROLES.has(r));
  const canAdmin = networkWide && roles.has('network_admin');

  const visible = TABS.filter((tab) =>
    tab.needs === 'send' ? canSend : tab.needs === 'view' ? canView : canAdmin,
  );
  const [active, setActive] = useTab<TabKey>(
    'comms',
    visible.map((tab) => tab.key),
    visible[0]?.key ?? 'log',
  );
  const current = visible.find((tab) => tab.key === active) ?? visible[0];

  return (
    <ModulePage
      title={title}
      subtitle={t('comms.subtitle')}
      tabs={visible.map((tab) => ({ key: tab.key, label: t(tab.label) }))}
      activeTab={current?.key ?? active}
      onTab={setActive}
    >
      {visible.length === 0 ? (
        <Notice tone="warn">{t('comms.common.forbidden')}</Notice>
      ) : (
        <>
          {current?.key === 'templates' ? <TemplatesView t={t} locale={locale} /> : null}
          {current?.key === 'triggers' ? <TriggersView t={t} locale={locale} /> : null}
          {current?.key === 'broadcast' ? <BroadcastView t={t} locale={locale} /> : null}
          {current?.key === 'log' ? <LogView t={t} locale={locale} /> : null}
          {current?.key === 'providers' ? <ProvidersView t={t} locale={locale} /> : null}
        </>
      )}
    </ModulePage>
  );
}
